import { useEffect, useMemo, useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import { listTodos, updateTodoAssignee, updateTodoDueDate, updateTodoStatus } from '../api/entries'
import type { Entry } from '../api/entries'
import { isOverdue } from '../utils/dates'
import { EntryComposer } from '../components/Entry/EntryComposer'
import { useAuthorTheme } from '../hooks/useAuthorTheme'
import { usePageHeader } from '../hooks/usePageHeader'
import { useUsername } from '../hooks/useUsername'
import { useAuthStore } from '../store/auth'

const STATUS_ORDER = ['wip', 'open', 'done', 'cancelled']

function groupByStatus(entries: Entry[]): Record<string, Entry[]> {
  const groups: Record<string, Entry[]> = {}
  for (const status of STATUS_ORDER) groups[status] = []
  for (const e of entries) {
    const s = e.todo_status ?? 'open'
    if (!groups[s]) groups[s] = []
    groups[s].push(e)
  }
  return groups
}

interface TodoItemProps {
  entry: Entry
  isOwn: boolean
  onStatusChange: (entry: Entry, status: string) => void
  onAssigneeChange: (entry: Entry, assignee: string | null) => void
  onDueDateChange: (entry: Entry, dueDate: string | null) => void
}

function TodoItem({ entry, isOwn, onStatusChange, onAssigneeChange, onDueDateChange }: TodoItemProps) {
  const [editingAssignee, setEditingAssignee] = useState(false)
  const [assigneeInput, setAssigneeInput] = useState(entry.todo_assignee_username ?? '')

  function submitAssignee(e: React.FormEvent) {
    e.preventDefault()
    onAssigneeChange(entry, assigneeInput.trim() || null)
    setEditingAssignee(false)
  }

  const isDone = entry.todo_status === 'done' || entry.todo_status === 'cancelled'
  const overdue = isOverdue(entry.todo_due_date, entry.todo_status)

  return (
    <div style={{ borderBottom: '1px solid color-mix(in srgb, var(--color-fg) 20%, transparent)', padding: '0.6rem 0', display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
      {isOwn ? (
        <select
          value={entry.todo_status ?? 'open'}
          onChange={(e) => onStatusChange(entry, e.target.value)}
          style={{ width: 'auto', fontSize: '0.8rem', flexShrink: 0 }}
        >
          {STATUS_ORDER.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
      ) : (
        <span style={{ fontSize: '0.8rem', opacity: 0.7, flexShrink: 0 }}>{entry.todo_status ?? 'open'}</span>
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        <Link
          to={`/~${entry.username}/entry/${entry.id}`}
          style={{
            fontWeight: isDone ? 'normal' : 'bold',
            textDecoration: isDone ? 'line-through' : 'none',
            opacity: entry.todo_status === 'cancelled' ? 0.5 : 1,
          }}
        >
          {entry.name || '(untitled)'}
        </Link>
        <div style={{ fontSize: '0.8rem', opacity: 0.6, marginTop: '0.15rem', display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'center' }}>
          <span>{new Date(entry.created_at).toLocaleDateString()}</span>
          {isOwn ? (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem', opacity: 1 }}>
              <span style={{ opacity: overdue ? 1 : 0.6, color: overdue ? 'var(--color-1)' : 'inherit' }}>
                {entry.todo_due_date ? `due ${entry.todo_due_date}` : 'no due date'}
              </span>
              <input
                type="date"
                value={entry.todo_due_date ?? ''}
                onChange={(e) => onDueDateChange(entry, e.target.value || null)}
                aria-label="due date"
                style={{ fontSize: '0.75rem', padding: '0 0.2rem', width: 'auto' }}
              />
            </span>
          ) : (
            entry.todo_due_date && (
              <span style={{ opacity: overdue ? 1 : 0.6, color: overdue ? 'var(--color-1)' : 'inherit' }}>due {entry.todo_due_date}</span>
            )
          )}
          {editingAssignee ? (
            <form onSubmit={submitAssignee} style={{ display: 'inline-flex', gap: '0.3rem' }}>
              <input
                value={assigneeInput}
                onChange={(e) => setAssigneeInput(e.target.value)}
                placeholder="username"
                style={{ fontSize: '0.8rem', padding: '0.1rem 0.3rem', width: 120 }}
                autoFocus
              />
              <button type="submit" style={{ fontSize: '0.75rem', width: 'auto', padding: '0.1rem 0.4rem' }}>ok</button>
              <button type="button" onClick={() => setEditingAssignee(false)} style={{ fontSize: '0.75rem', background: 'transparent', color: 'var(--color-fg)', width: 'auto', padding: '0.1rem 0.2rem' }}>×</button>
            </form>
          ) : (
            <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
              {entry.todo_assignee_username && <span>→ {entry.todo_assignee_username}</span>}
              {isOwn && (
                <button
                  type="button"
                  onClick={() => { setAssigneeInput(entry.todo_assignee_username ?? ''); setEditingAssignee(true) }}
                  style={{ background: 'transparent', color: 'var(--color-fg)', width: 'auto', fontSize: '0.75rem', padding: '0 0.15rem', opacity: 0.5 }}
                >
                  {entry.todo_assignee_username ? 'edit' : 'assign'}
                </button>
              )}
            </span>
          )}
        </div>
      </div>
    </div>
  )
}

export function Todos() {
  const usernameParam = useUsername()
  const { username } = useParams<{ username: string }>()
  const resolvedUsername = username?.replace(/^~/, '') ?? ''
  const viewerUsername = useAuthStore((s) => s.username)
  const isOwn = resolvedUsername === viewerUsername
  const [searchParams] = useSearchParams()
  const initialTag = searchParams.get('initialTag') ?? undefined
  useAuthorTheme(usernameParam ?? undefined)

  const titleNode = useMemo(() => (
    usernameParam ? (
      <>
        <Link to={`/~${usernameParam}`} style={{ color: 'inherit', textDecoration: 'none', opacity: 0.6 }}>~{usernameParam}</Link>
        {' / todos'}
      </>
    ) : 'todos'
  ), [usernameParam])

  usePageHeader(titleNode)

  const [todos, setTodos] = useState<Entry[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    if (!resolvedUsername) return
    listTodos(resolvedUsername)
      .then(setTodos)
      .catch((err) => setError(err instanceof Error ? err.message : 'failed to load'))
      .finally(() => setLoading(false))
  }, [resolvedUsername])

  async function handleStatusChange(entry: Entry, status: string) {
    try {
      const updated = await updateTodoStatus(entry.id, status)
      setTodos((prev) => prev.map((e) => (e.id === entry.id ? updated : e)))
    } catch { /* ignore */ }
  }

  async function handleAssigneeChange(entry: Entry, assignee: string | null) {
    try {
      const updated = await updateTodoAssignee(entry.id, assignee)
      setTodos((prev) => prev.map((e) => (e.id === entry.id ? updated : e)))
    } catch { /* ignore */ }
  }

  async function handleDueDateChange(entry: Entry, dueDate: string | null) {
    try {
      const updated = await updateTodoDueDate(entry.id, dueDate)
      setTodos((prev) => prev.map((e) => (e.id === entry.id ? updated : e)))
    } catch { /* ignore */ }
  }

  const groups = groupByStatus(todos)

  return (
    <div>
      {isOwn && (
        <EntryComposer
          entryType="todo"
          initialTags={initialTag ? [initialTag] : undefined}
          onCreated={(e) => setTodos((prev) => [e, ...prev])}
        />
      )}
      {loading && <p style={{ opacity: 0.6 }}>loading…</p>}
      {error && <p style={{ color: 'var(--color-1)' }}>{error}</p>}
      {!loading && !error && todos.length === 0 && (
        <p style={{ opacity: 0.6 }}>no todos yet.</p>
      )}
      {STATUS_ORDER.map((status) => {
        const items = groups[status]
        if (!items || items.length === 0) return null
        return (
          <section key={status} style={{ marginBottom: '1.5rem' }}>
            <h2 style={{ fontSize: '0.85rem', opacity: 0.6, margin: '0 0 0.25rem' }}>{status}</h2>
            {items.map((entry) => (
              <TodoItem
                key={entry.id}
                entry={entry}
                isOwn={isOwn}
                onStatusChange={handleStatusChange}
                onAssigneeChange={handleAssigneeChange}
                onDueDateChange={handleDueDateChange}
              />
            ))}
          </section>
        )
      })}
    </div>
  )
}
